(() => {
  const initializedLandings = new WeakSet();
  let submitHandlerAttached = false;

  function emit(name, detail = {}) {
    const payload = { ...detail, experiment: 'pro-pair-offer-2026-09-17', arm: 'dedicated_offer', path: window.location.pathname };
    document.dispatchEvent(new CustomEvent(name, { detail: payload }));
    try { window.Shopify?.analytics?.publish?.('field_signal:' + name.replace('fieldsignal:', ''), payload); }
    catch (_) { /* Analytics must never interrupt purchase behavior. */ }
  }

  function escapeSelector(value) {
    if (window.CSS?.escape) return window.CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function closestProductForm(element, landing) {
    const directScope = element.closest('[data-fsc-pair-form]');
    if (directScope) return directScope;

    const panel = element.closest('[data-fs-ad-panel]');
    if (panel) {
      const panelScope = panel.querySelector('[data-fsc-pair-form]');
      if (panelScope) return panelScope;
    }

    return landing.querySelector('[data-fsc-pair-form]');
  }

  function offerScope(offer, productForm, landing) {
    return (
      offer.closest('[data-fs-ad-offers]') ||
      offer.closest('[data-fs-ad-panel]') ||
      productForm ||
      landing
    );
  }

  function getOfferVariantId(offer) {
    return (
      offer.dataset.fsAdVariant ||
      offer.dataset.fsAdVariantId ||
      offer.dataset.variantId ||
      offer.getAttribute('data-variant-id') ||
      offer.dataset.fsAdOffer ||
      ''
    );
  }

  function protectionMap(productForm) {
    const mappingNode = productForm.querySelector('[data-fs-ad-protection-variants]');
    if (!mappingNode) return {};

    try {
      return JSON.parse(mappingNode.textContent || '{}');
    } catch (_) {
      return {};
    }
  }

  function syncProtectionChoice(choice) {
    const protection = choice?.closest('[data-fs-ad-protection]');
    if (!protection) return;

    protection.querySelectorAll('[data-fs-ad-protection-card]').forEach((card) => {
      card.toggleAttribute(
        'data-fs-ad-protection-selected',
        Boolean(card.querySelector('[data-fs-ad-protection-choice]:checked'))
      );
    });

    const productForm = protection.closest('[data-fsc-pair-form]');
    const planSelected =
      choice.matches('[data-fs-ad-protection-choice="plan"]') &&
      choice.checked &&
      !choice.disabled;
    productForm?.toggleAttribute('data-fs-ad-protection-selected', planSelected);
  }

  function setProtectionPlanForVariant(productForm, productVariantId) {
    const planChoice = productForm.querySelector('[data-fs-ad-protection-choice="plan"]');
    if (!planChoice) return;

    const protection = protectionMap(productForm)[String(productVariantId || '')];
    const choiceCard = planChoice.closest('[data-fs-ad-protection-card]');

    if (!protection?.id) {
      planChoice.disabled = true;
      choiceCard?.setAttribute('data-fs-ad-protection-unavailable', 'true');

      if (planChoice.checked) {
        planChoice.checked = false;
        syncProtectionChoice(planChoice);
      }
      return;
    }

    planChoice.disabled = false;
    planChoice.dataset.fsAdProtectionVariant = String(protection.id);
    planChoice.dataset.fsAdProtectionTitle =
      protection.title || 'Field Signal Extreme Protection Plan';
    choiceCard?.removeAttribute('data-fs-ad-protection-unavailable');

    productForm.querySelectorAll('[data-fs-ad-protection-price]').forEach((price) => {
      price.textContent = protection.price || '';
    });
    productForm.querySelectorAll('[data-fs-ad-protection-monthly]').forEach((monthly) => {
      monthly.textContent = protection.monthly || '';
    });
  }

  function syncPdpMetrics(landing, selectedTab) {
    if (!landing || !selectedTab) return;

    const facts = {
      response: {
        value: selectedTab.dataset.fsPdpResponse || '',
        copy: selectedTab.dataset.fsPdpResponseCopy || '',
      },
      protection: {
        value: selectedTab.dataset.fsPdpProtection || '',
        copy: selectedTab.dataset.fsPdpProtectionCopy || '',
      },
      nrr: {
        value: selectedTab.dataset.fsPdpNrr || '',
        copy: selectedTab.dataset.fsPdpNrrCopy || '',
      },
      battery: {
        value: selectedTab.dataset.fsPdpBattery || '',
        copy: selectedTab.dataset.fsPdpBatteryCopy || '',
      },
    };

    Object.entries(facts).forEach(([name, fact]) => {
      landing.querySelectorAll(`[data-fs-pdp-metric-value="${name}"]`).forEach((node) => {
        node.textContent = fact.value;
      });
      landing.querySelectorAll(`[data-fs-pdp-metric-copy="${name}"]`).forEach((node) => {
        node.textContent = fact.copy;
      });
    });

    const hardware = landing.querySelector('.fs-pdp-hardware');
    const hardwareImage = hardware?.querySelector('.fs-pdp-hardware__grid > figure img');
    const hardwareSource = versionImageSource(hardware, selectedTab.dataset.fsAdVersion);
    if (hardwareImage && hardwareSource) {
      hardwareImage.src = hardwareSource;
      hardwareImage.alt = selectedTab.dataset.fsPdpAlt || hardwareImage.alt;
    }
  }

  function versionImageSource(hardware, version) {
    if (!hardware) return '';
    return version === 'standard'
      ? hardware.dataset.fsPdpHardwareStandard || ''
      : hardware.dataset.fsPdpHardwarePro || '';
  }

  function setGalleryImage(gallery, index, shouldScroll = true) {
    const lead = gallery?.querySelector('[data-fs-pdp-gallery-lead]');
    const thumbs = Array.from(gallery?.querySelectorAll('[data-fs-pdp-gallery-thumb]') || []);
    if (!lead || !thumbs.length) return;

    const normalizedIndex = (index + thumbs.length) % thumbs.length;
    const selectedThumb = thumbs[normalizedIndex];
    const source = selectedThumb.dataset.fsPdpGallerySrc || '';
    const alt = selectedThumb.dataset.fsPdpGalleryAlt || lead.alt || '';

    if (source) lead.src = source;
    lead.alt = alt;

    const zoomImage = gallery.querySelector('[data-fs-pdp-gallery-zoom-image]');
    if (zoomImage) {
      zoomImage.src = source;
      zoomImage.alt = alt;
    }

    thumbs.forEach((thumb, thumbIndex) => {
      const selected = thumbIndex === normalizedIndex;
      thumb.classList.toggle('is-selected', selected);
      thumb.setAttribute('aria-pressed', String(selected));
    });

    const status = gallery.querySelector('[data-fs-pdp-gallery-status]');
    if (status) status.textContent = `${normalizedIndex + 1} / ${thumbs.length}`;

    if (shouldScroll) {
      selectedThumb.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  function initializeGallery(landing) {
    landing.querySelectorAll('[data-fs-ad-gallery-panel]').forEach((gallery) => {
      const thumbs = Array.from(gallery.querySelectorAll('[data-fs-pdp-gallery-thumb]'));
      const selectedIndex = Math.max(
        0,
        thumbs.findIndex((thumb) => thumb.classList.contains('is-selected'))
      );
      setGalleryImage(gallery, selectedIndex, false);
    });
  }

  function getStatus(productForm, landing) {
    const status =
      productForm?.querySelector('[data-fs-ad-status]') ||
      productForm?.closest('[data-fs-ad-panel]')?.querySelector('[data-fs-ad-status]') ||
      landing.querySelector('[data-fs-ad-status]');

    if (status) {
      status.setAttribute('aria-live', 'polite');
    }

    return status;
  }

  function setStatus(status, message, isError = false) {
    if (!status) return;

    status.hidden = false;
    status.textContent = message;
    status.setAttribute('role', isError ? 'alert' : 'status');
  }

  function lineItemProperties(productForm) {
    const properties = {};
    const formData = new FormData(productForm);

    formData.forEach((value, key) => {
      const match = /^properties\[([^\]]+)\]$/.exec(key);
      const normalizedValue = typeof value === 'string' ? value.trim() : '';
      if (!match || !normalizedValue) return;
      properties[match[1]] = normalizedValue;
    });

    return properties;
  }

  function setSelectedOffer(offer, landing) {
    const productForm = closestProductForm(offer, landing);
    if (!productForm) return;

    const variantInput = productForm.querySelector('input[data-fs-ad-variant-input]');
    const variantId = getOfferVariantId(offer);
    const scope = offerScope(offer, productForm, landing);

    scope.querySelectorAll('[data-fs-ad-offer]').forEach((candidate) => {
      const selected = candidate === offer;
      candidate.setAttribute('aria-pressed', String(selected));
      candidate.toggleAttribute('data-fs-ad-offer-selected', selected);
      candidate.classList.toggle('is-selected', selected);
    });

    if (variantInput && variantId) {
      variantInput.value = variantId;
      variantInput.dispatchEvent(new Event('input', { bubbles: true }));
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      setProtectionPlanForVariant(productForm, variantId);
    }

    const price = offer.dataset.fsAdPrice || offer.getAttribute('data-price') || '';
    const compare = offer.dataset.fsAdCompare || offer.getAttribute('data-compare') || '';
    const displayScope = productForm.closest('[data-fs-ad-panel]') || productForm;

    displayScope.querySelectorAll('[data-fs-ad-selected-price]').forEach((node) => {
      node.textContent = price;
    });

    displayScope.querySelectorAll('[data-fs-ad-selected-compare]').forEach((node) => {
      node.textContent = compare;
      node.hidden = !compare;
    });

    syncPairOffer(productForm, landing);
    emit('fieldsignal:bundle_select', {
      variantId,
      label: offer.dataset.fsAdOfferLabel || offer.textContent.trim(),
      price,
      compare,
    });
  }

  function activateVersion(landing, selectedTab, moveFocus = false) {
    const tabs = Array.from(
      landing.querySelectorAll('[role="tab"][data-fs-ad-version]')
    );
    if (!tabs.length || !selectedTab) return;

    const version = selectedTab.dataset.fsAdVersion || '';
    const controlledPanelId = selectedTab.getAttribute('aria-controls');
    const controlledPanel = controlledPanelId ? document.getElementById(controlledPanelId) : null;

    tabs.forEach((tab) => {
      const selected = tab === selectedTab;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      tab.toggleAttribute('data-fs-ad-version-selected', selected);
    });

    const panels = Array.from(landing.querySelectorAll('[data-fs-ad-panel]'));
    const matchingPanels = panels.filter((panel) => {
      if (controlledPanel && panel === controlledPanel) return true;
      return (
        panel.dataset.fsAdPanel === version ||
        panel.dataset.fsAdVersion === version
      );
    });

    if (matchingPanels.length) {
      panels.forEach((panel) => {
        const selected = matchingPanels.includes(panel);
        panel.hidden = !selected;
        panel.setAttribute('aria-hidden', String(!selected));
      });
    }

    const heading = landing.querySelector('[data-fs-pdp-heading]');
    if (heading) {
      heading.textContent = selectedTab.dataset.fsPdpTitle || '';
    }

    syncPdpMetrics(landing, selectedTab);
    emit('fieldsignal:version_change', { version });

    if (moveFocus) selectedTab.focus();
  }

  function focusScrollTarget(target) {
    if (!target) return;

    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center',
    });

    window.setTimeout(() => {
      const focusTarget =
        target.matches('input, select, textarea, button, a[href]')
          ? target
          : target.querySelector(
              'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href]'
            ) || target;

      if (!focusTarget.hasAttribute('tabindex') && focusTarget === target) {
        focusTarget.setAttribute('tabindex', '-1');
      }
      if (typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      }
    }, 450);
  }

  function resolveScrollTarget(trigger, landing) {
    const targetReference = trigger.dataset.fsAdScrollToOffer || '';
    if (targetReference) {
      try {
        const target = landing.querySelector(targetReference);
        if (target) return target;
      } catch (_) {
        // Treat non-selector values as an id or an offer target name below.
      }

      const id = targetReference.replace(/^#/, '');
      const byId = document.getElementById(id);
      if (byId && landing.contains(byId)) return byId;

      const namedTarget = landing.querySelector(
        `[data-fs-ad-offer-target="${escapeSelector(targetReference)}"]`
      );
      if (namedTarget) return namedTarget;
    }

    return landing.querySelector('[data-fsc-pair-form]') || landing.querySelector('[data-fs-ad-offer]');
  }

  function handleLandingClick(event, landing) {
    const zoomOpen = event.target.closest('[data-fs-pdp-gallery-zoom-open]');
    if (zoomOpen && landing.contains(zoomOpen)) {
      event.preventDefault();
      const dialog = zoomOpen.closest('[data-fs-ad-gallery-panel]')?.querySelector('[data-fs-pdp-gallery-zoom]');
      if (dialog?.showModal) dialog.showModal();
      return;
    }

    const zoomClose = event.target.closest('[data-fs-pdp-gallery-zoom-close]');
    if (zoomClose && landing.contains(zoomClose)) {
      event.preventDefault();
      zoomClose.closest('[data-fs-pdp-gallery-zoom]')?.close();
      return;
    }

    const quickControl = event.target.closest('[data-fs-pdp-quick-prev], [data-fs-pdp-quick-next]');
    if (quickControl && landing.contains(quickControl)) {
      event.preventDefault();
      const nav = quickControl.closest('.fs-pdp-quick-links__rail')?.querySelector('[data-fs-pdp-quick-nav]');
      const direction = quickControl.matches('[data-fs-pdp-quick-next]') ? 1 : -1;
      nav?.scrollBy({ left: direction * Math.max(180, nav.clientWidth * 0.72), behavior: 'smooth' });
      return;
    }

    const galleryControl = event.target.closest('[data-fs-pdp-gallery-prev], [data-fs-pdp-gallery-next]');
    if (galleryControl && landing.contains(galleryControl)) {
      event.preventDefault();
      const gallery = galleryControl.closest('[data-fs-ad-gallery-panel]');
      const thumbs = Array.from(gallery?.querySelectorAll('[data-fs-pdp-gallery-thumb]') || []);
      const selectedIndex = thumbs.findIndex((thumb) => thumb.classList.contains('is-selected'));
      const direction = galleryControl.matches('[data-fs-pdp-gallery-next]') ? 1 : -1;
      // Keep the page still when swiping the lead image on a touch screen.
      setGalleryImage(gallery, selectedIndex + direction, !galleryControl.hasAttribute('data-fs-pdp-gallery-swipe'));
      return;
    }

    const galleryThumb = event.target.closest('[data-fs-pdp-gallery-thumb]');
    if (galleryThumb && landing.contains(galleryThumb)) {
      event.preventDefault();
      const gallery = galleryThumb.closest('[data-fs-ad-gallery-panel]');
      const thumbs = Array.from(gallery?.querySelectorAll('[data-fs-pdp-gallery-thumb]') || []);
      setGalleryImage(gallery, thumbs.indexOf(galleryThumb));
      return;
    }

    const versionTab = event.target.closest('[role="tab"][data-fs-ad-version]');
    if (versionTab && landing.contains(versionTab)) {
      event.preventDefault();
      activateVersion(landing, versionTab, true);
      return;
    }

    const offer = event.target.closest('[data-fs-ad-offer]');
    if (offer && landing.contains(offer)) {
      event.preventDefault();
      setSelectedOffer(offer, landing);
      return;
    }

    const scrollTrigger = event.target.closest('[data-fs-ad-scroll-to-offer]');
    if (scrollTrigger && landing.contains(scrollTrigger)) {
      event.preventDefault();
      focusScrollTarget(resolveScrollTarget(scrollTrigger, landing));
    }
  }

  function handleLandingKeydown(event, landing) {
    const galleryThumb = event.target.closest('[data-fs-pdp-gallery-thumb]');
    if (galleryThumb && landing.contains(galleryThumb)) {
      const gallery = galleryThumb.closest('[data-fs-ad-gallery-panel]');
      const thumbs = Array.from(gallery?.querySelectorAll('[data-fs-pdp-gallery-thumb]') || []);
      const currentIndex = thumbs.indexOf(galleryThumb);
      let nextIndex;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = currentIndex - 1;
      else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = currentIndex + 1;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = thumbs.length - 1;
      else return;

      event.preventDefault();
      setGalleryImage(gallery, nextIndex);
      thumbs[(nextIndex + thumbs.length) % thumbs.length]?.focus();
      return;
    }

    const tab = event.target.closest('[role="tab"][data-fs-ad-version]');
    if (!tab || !landing.contains(tab)) return;

    const tabs = Array.from(
      landing.querySelectorAll('[role="tab"][data-fs-ad-version]')
    );
    const currentIndex = tabs.indexOf(tab);
    if (currentIndex < 0) return;

    let nextIndex;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    activateVersion(landing, tabs[nextIndex], true);
  }

  function handleLandingChange(event, landing) {
    const paidProtection = event.target.closest('[data-fsc-pair-paid-protection]');
    if (paidProtection && landing.contains(paidProtection)) {
      const productForm = paidProtection.closest('[data-fsc-pair-form]');
      if (!productForm) return;
      syncPairOffer(productForm, landing);
      const selected = productForm.querySelector('[data-fs-ad-offer-selected]');
      const config = pairConfig(productForm);
      emit('fieldsignal:protection_plan_select', {
        selected: selected?.dataset.fscPairOption === 'single' && paidProtection.checked,
        variantId: String(config.paidProtection?.id || ''),
        label: '2-year protection',
        price: config.paidProtection?.price / 100,
      });
      return;
    }
    const colorChoice = event.target.closest('[data-fs-pdp-color]');
    if (colorChoice && landing.contains(colorChoice) && colorChoice.checked) {
      const productForm = colorChoice.closest('[data-fsc-pair-form]');
      productForm?.querySelectorAll('[data-fs-pdp-color-name]').forEach((node) => {
        node.textContent = colorChoice.value;
      });
      emit('fieldsignal:variant_select', {
        option: 'Color',
        value: colorChoice.value,
      });
      return;
    }

    const choice = event.target.closest('[data-fs-ad-protection-choice]');
    if (!choice || !landing.contains(choice)) return;

    syncProtectionChoice(choice);
    emit('fieldsignal:protection_plan_select', {
      selected:
        choice.matches('[data-fs-ad-protection-choice="plan"]') &&
        choice.checked &&
        !choice.disabled,
      variantId: choice.dataset.fsAdProtectionVariant || '',
      label: choice.dataset.fsAdProtectionTitle || '',
    });
  }

  function resetSubmitState(productForm) {
    productForm.removeAttribute('aria-busy');
    const submitButton = productForm.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-label');
    }
  }

  function pairConfig(form) {
    return JSON.parse(form.querySelector('[data-fsc-pair-config]')?.textContent || '{}');
  }

  function paidProtectionSelected(form, option) {
    return option === 'single' && Boolean(form.querySelector('[data-fsc-pair-paid-protection]')?.checked);
  }

  function syncPairOffer(form, landing) {
    const selected = form.querySelector('[data-fs-ad-offer-selected]');
    const option = selected?.dataset.fscPairOption || 'single';
    const duo = option === 'duo';
    const config = pairConfig(form);
    const core = window.FieldSignalPairOfferCore;
    const choice = form.querySelector('[data-fsc-pair-paid-protection]');
    const paid = paidProtectionSelected(form, option);
    let paidUnavailable = false;
    try { core.validate(config, 'single', true); }
    catch (_) { paidUnavailable = true; }
    const busy = form.getAttribute('aria-busy') === 'true';
    // A restored opt-in must remain removable even if that plan is unavailable.
    if (choice) choice.disabled = duo || (paidUnavailable && !choice.checked) || busy;
    let unavailable = '';
    let total = 0;
    try { total = core.total(config, option, paid); }
    catch (error) { unavailable = error.message; }
    const status = getStatus(form, landing);
    if (unavailable) setStatus(status, unavailable, true);
    else if (status) { status.hidden = true; status.textContent = ''; }
    form.querySelector('[type="submit"]').disabled = Boolean(unavailable) || busy;
    const sticky = landing.querySelector('[data-fs-pdp-sticky-submit]');
    if (sticky) sticky.disabled = Boolean(unavailable) || busy;
    const singlePanel = form.querySelector('[data-fsc-pair-coverage-single]');
    const duoPanel = form.querySelector('[data-fsc-pair-coverage-duo]');
    if (singlePanel) singlePanel.hidden = duo;
    if (duoPanel) duoPanel.hidden = !duo;
    const coverage = form.querySelector('[data-fsc-pair-coverage]');
    if (coverage) {
      coverage.dataset.fscPairCoverage = duo ? 'included' : paid ? 'selected' : 'optional';
    }
    const summary = form.querySelector('[data-fsc-pair-single-plan-summary]');
    if (summary) summary.textContent = choice?.checked ? '2-year protection added' : 'Protection plan optional';
    const planPrice = form.querySelector('[data-fsc-pair-paid-protection-price]');
    if (planPrice) planPrice.textContent = `$${(core.paidProtectionPrice / 100).toFixed(2)}`;
    const planStatus = form.querySelector('[data-fsc-pair-protection-status]');
    if (planStatus) planStatus.textContent = paidUnavailable ? 'Protection is temporarily unavailable' : paid ? 'Added to your order' : 'Optional · select to add';
    const label = form.querySelector('[data-fsc-pair-submit-label]');
    const amount = total % 100 === 0 ? String(total / 100) : (total / 100).toFixed(2);
    if (label) label.textContent = unavailable ? 'Offer unavailable' : `${duo ? 'Get 2 Pro pairs' : paid ? 'Get 1 pair + plan' : 'Get 1 Pro pair'} — $${amount}`;
    const stickyPrice = landing.querySelector('[data-fs-pdp-sticky-price]');
    const stickyPlan = landing.querySelector('[data-fs-pdp-sticky-plan]');
    const stickyVersion = landing.querySelector('[data-fs-pdp-sticky-version]');
    if (stickyPrice) stickyPrice.textContent = unavailable ? 'Unavailable' : `$${(total / 100).toFixed(2)}`;
    if (stickyPlan) stickyPlan.textContent = duo ? 'Free 2-year protection for both' : paid ? '2-year protection added' : 'No added protection plan';
    if (stickyVersion) stickyVersion.textContent = duo ? '2 Pro pairs' : '1 Pro pair';
  }

  function pairAttribution() {
    const query = new URLSearchParams(window.location.search);
    const properties = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ad_id', 'adset_id', 'campaign_id'].forEach(key => {
      const value = query.get(key);
      if (value) properties[`_${key}`] = value.slice(0, 240);
    });
    return properties;
  }

  async function submitToCheckout(event, productForm, landing) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (productForm.getAttribute('aria-busy') === 'true') return;
    const selected = productForm.querySelector('[data-fs-ad-offer-selected]');
    const option = selected?.dataset.fscPairOption || 'single';
    const config = pairConfig(productForm);
    const paid = paidProtectionSelected(productForm, option);
    const color = productForm.querySelector('[data-fs-pdp-color]:checked')?.value;
    const status = getStatus(productForm, landing);
    const submit = productForm.querySelector('[type="submit"]');
    const sticky = landing.querySelector('[data-fs-pdp-sticky-submit]');
    const reference = productForm.dataset.fscPairPendingRef || crypto.randomUUID();
    productForm.dataset.fscPairPendingRef = reference;
    const root = window.Shopify?.routes?.root || '/';
    const route = root.endsWith('/') ? root : `${root}/`;
    const request = async (path, body) => {
      const response = await fetch(`${route}${path}`, body ? {
        method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || 'Your cart could not be updated. Please try again.');
      return payload;
    };
    try {
      const core = window.FieldSignalPairOfferCore;
      const items = core.buildItems(config, option, color, reference, pairAttribution(), paid);
      productForm.setAttribute('aria-busy', 'true');
      submit.disabled = true;
      if (sticky) sticky.disabled = true;
      const paidChoice = productForm.querySelector('[data-fsc-pair-paid-protection]');
      if (paidChoice) paidChoice.disabled = true;
      setStatus(status, 'Preparing your selected offer…');
      // Preserve unrelated cart lines and reuse a confirmed pending addition on retry.
      let cart = await request('cart.js');
      const existing = (cart.items || []).filter(item => item.properties?._fs_offer_ref === reference);
      if (existing.length) {
        let sameSelection = false;
        try {
          core.validateCart(cart, config, option, reference, paid);
          sameSelection = existing.some(item => Number(item.variant_id) === Number(config[option].id) && !item.parent_relationship && item.properties?.Color === color && item.properties?.Protection === items[0].properties.Protection);
        } catch (_) { /* A changed or incomplete pending selection must be reconciled. */ }
        if (!sameSelection) {
          const updates = {};
          existing.filter(item => !item.parent_relationship).forEach(item => { updates[item.key] = 0; });
          if (!Object.keys(updates).length) throw new Error('Your previous selection could not be updated. Please review your cart before continuing.');
          const updated = await request('cart/update.js', { updates });
          // Shopify removes native children with their parent. Confirm cleanup
          // before adding the new selection so a retry cannot charge both plans.
          if (!Array.isArray(updated.items) || updated.items.some(item => item.properties?._fs_offer_ref === reference)) {
            throw new Error('Your previous selection could not be updated. Please review your cart before continuing.');
          }
          await request('cart/add.js', { items });
        }
      } else await request('cart/add.js', { items });
      cart = await request('cart.js');
      core.validateCart(cart, config, option, reference, paid);
      emit('fieldsignal:checkout_start', { experiment: 'pro-pair-offer-2026-09-17', arm: 'dedicated_offer', option, productVariantId: String(config[option].id), protectionSelected: option === 'duo' || paid, value: core.total(config, option, paid) / 100, currency: 'USD' });
      window.location.assign(`${route}checkout`);
    } catch (error) {
      productForm.removeAttribute('aria-busy');
      syncPairOffer(productForm, landing);
      setStatus(status, error.message || 'Your cart could not be updated. Please try again.', true);
    }
  }


  function attachSubmitHandler() {
    if (submitHandlerAttached) return;
    submitHandlerAttached = true;

    // Horizon handles product submits from document capture. Window capture lets
    // this landing page add the exact selection before opening checkout.
    window.addEventListener(
      'submit',
      (event) => {
        const form = event.target instanceof Element ? event.target.closest('form') : null;
        const productForm =
          form?.matches('[data-fsc-pair-form]')
            ? form
            : form?.closest('[data-fsc-pair-form]');
        const landing = productForm?.closest('[data-fsc-pair-landing]');
        if (!form || !productForm || !landing) return;
        submitToCheckout(event, productForm, landing);
      },
      true
    );
  }

  function initializeMidnightSaleTimer(landing) {
    const timer = landing.querySelector('[data-fs-pdp-sale-timer]');
    const countdown = timer?.querySelector('[data-fs-pdp-sale-countdown]');
    if (!timer || !countdown || timer.dataset.fsPdpSaleTimerInitialized === 'true') return;

    timer.dataset.fsPdpSaleTimerInitialized = 'true';

    const pad = (value) => String(value).padStart(2, '0');
    const nextLocalMidnight = () => {
      const target = new Date();
      target.setHours(24, 0, 0, 0);
      return target;
    };

    let target = nextLocalMidnight();

    const update = () => {
      let remaining = target.getTime() - Date.now();
      if (remaining <= 0) {
        target = nextLocalMidnight();
        remaining = target.getTime() - Date.now();
      }

      const totalSeconds = Math.max(0, Math.floor(remaining / 1000));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const label = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

      countdown.textContent = label;
      timer.setAttribute(
        'aria-label',
        `Pre-season sale ends in ${hours} hours, ${minutes} minutes, and ${seconds} seconds`
      );
    };

    update();
    window.setInterval(update, 1000);
  }

  function initializeLanding(landing) {
    if (initializedLandings.has(landing)) return;
    initializedLandings.add(landing);
    initializeMidnightSaleTimer(landing);

    const tabs = Array.from(
      landing.querySelectorAll('[role="tab"][data-fs-ad-version]')
    );

    landing.querySelectorAll('[data-fs-ad-panel]:not([data-fs-ad-gallery-panel])').forEach((panel) => {
      if (!panel.hasAttribute('role')) panel.setAttribute('role', 'tabpanel');
    });

    const requestedVersion = new URLSearchParams(window.location.search)
      .get('version')
      ?.trim()
      .toLowerCase();
    const requestedTab = requestedVersion
      ? tabs.find(
          (tab) => (tab.dataset.fsAdVersion || '').trim().toLowerCase() === requestedVersion
        )
      : null;
    const selectedTab =
      requestedTab || tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    if (selectedTab) activateVersion(landing, selectedTab);
    initializeGallery(landing);

    landing.querySelectorAll('[data-fsc-pair-form]').forEach((productForm) => {
      resetSubmitState(productForm);
      syncPairOffer(productForm, landing);
      const variantId = productForm.querySelector('[data-fs-ad-variant-input]')?.value;
      setProtectionPlanForVariant(productForm, variantId);

      const selectedChoice = productForm.querySelector(
        '[data-fs-ad-protection-choice]:checked'
      );
      if (selectedChoice) syncProtectionChoice(selectedChoice);

      productForm.addEventListener('submit', (event) => {
        const selectedPlan = productForm.querySelector(
          '[data-fs-ad-protection-choice="plan"]:checked:not(:disabled)'
        );
        if (selectedPlan) return;

        if (productForm.getAttribute('aria-busy') === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        productForm.setAttribute('aria-busy', 'true');
        const submitter = event.submitter || productForm.querySelector('[type="submit"]');
        if (submitter) submitter.disabled = true;
      });
    });

    landing.addEventListener('click', (event) => handleLandingClick(event, landing));
    landing.addEventListener('keydown', (event) => handleLandingKeydown(event, landing));
    landing.addEventListener('change', (event) => handleLandingChange(event, landing));
  }

  function initialize() {
    document.querySelectorAll('[data-fsc-pair-landing]').forEach(initializeLanding);
    attachSubmitHandler();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  // Browsers can restore the landing page from the back-forward cache with its
  // pre-navigation DOM state intact. Always restore the CTA when the page returns.
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-fsc-pair-form]').forEach(form => {
      resetSubmitState(form);
      syncPairOffer(form, form.closest('[data-fsc-pair-landing]'));
    });
  });
})();
